/**
 * @fileoverview Processing Status Badge
 *
 * Purpose: Visual indicator showing a document's processing pipeline state.
 * Displays animated spinners for in-progress states, coloured badges for
 * terminal states (ready/failed), and neutral for pending.
 * Now includes category extraction status awareness.
 *
 * @component
 * @example
 * <ProcessingStatusBadge
 *   extractionStatus="extracted"
 *   classificationStatus="classified"
 *   indexingStatus="indexed"
 *   categoryExtractionStatus="extracting"
 * />
 */

import { Badge } from "@/ui/primitives/badge";
import { Loader2, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { FAILURE_CLASS_META, type FailureClass } from "@cvx/lib/failureClasses";
import { getOverallStatus } from "../constants";

interface ProcessingStatusBadgeProps {
  extractionStatus?: string;
  classificationStatus?: string;
  indexingStatus?: string;
  categoryExtractionStatus?: string;
  /** Optional: show the specific stage name instead of overall status */
  showDetail?: boolean;
  // --- MOR-528: per-stage failure classes + messages for the failed tooltip ---
  extractionFailureClass?: FailureClass;
  classificationFailureClass?: FailureClass;
  indexingFailureClass?: FailureClass;
  categoryExtractionFailureClass?: FailureClass;
  extractionError?: string;
  classificationError?: string;
  indexingError?: string;
  categoryExtractionError?: string;
}

/**
 * Displays the document's processing status as a coloured badge.
 * Automatically derives the overall status from individual stage statuses.
 */
export function ProcessingStatusBadge({
  extractionStatus,
  classificationStatus,
  indexingStatus,
  categoryExtractionStatus,
  showDetail = false,
  extractionFailureClass,
  classificationFailureClass,
  indexingFailureClass,
  categoryExtractionFailureClass,
  extractionError,
  classificationError,
  indexingError,
  categoryExtractionError,
}: ProcessingStatusBadgeProps) {
  const overall = getOverallStatus(
    extractionStatus,
    classificationStatus,
    indexingStatus,
    categoryExtractionStatus,
  );

  // No pipeline status at all (Phase 1 documents)
  if (!extractionStatus && !classificationStatus && !indexingStatus) {
    return null;
  }

  if (overall === "ready") {
    return (
      <Badge color="emerald" variant="soft" className="gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Ready
      </Badge>
    );
  }

  if (overall === "failed") {
    // Resolve which stage failed and its structured failure class + message.
    const failed =
      extractionStatus === "failed"
        ? {
            stage: "Extraction",
            cls: extractionFailureClass,
            msg: extractionError,
          }
        : classificationStatus === "failed"
          ? {
              stage: "Classification",
              cls: classificationFailureClass,
              msg: classificationError,
            }
          : indexingStatus === "failed"
            ? {
                stage: "Indexing",
                cls: indexingFailureClass,
                msg: indexingError,
              }
            : categoryExtractionStatus === "failed"
              ? {
                  stage: "Metadata Extraction",
                  cls: categoryExtractionFailureClass,
                  msg: categoryExtractionError,
                }
              : {
                  stage: "Processing",
                  cls: undefined as FailureClass | undefined,
                  msg: undefined,
                };

    const meta = FAILURE_CLASS_META[failed.cls ?? "unknown"];
    // Pill shows the class label (concise) unless caller wants the stage name.
    const label = showDetail ? `${failed.stage} Failed` : meta.label;
    // Show the specific backend message only when it adds detail beyond the
    // hint. Suppress it for cost blocks — the raw message references the cost
    // limit, and we don't surface cost/credit details to the user.
    const showSpecific =
      !!failed.msg &&
      failed.cls !== "cost_limit" &&
      failed.msg.trim() !== meta.hint.trim();

    return (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <Badge variant="destructive" className="max-w-full gap-1">
                <AlertCircle className="h-3 w-3 shrink-0" />
                <span className="truncate">{label}</span>
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium">
                {failed.stage} failed: {meta.label}
              </p>
              <p className="text-popover-foreground/80">{meta.hint}</p>
              {showSpecific && (
                <p className="text-[11px] text-popover-foreground/60">
                  {failed.msg}
                </p>
              )}
              <p className="text-[11px] text-popover-foreground/60">
                {meta.retriable
                  ? "You can retry this document."
                  : "Retrying will not help — fix the file and re-upload."}
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (overall === "processing") {
    const currentStage =
      extractionStatus === "extracting"
        ? "Extracting"
        : classificationStatus === "classifying"
          ? "Classifying"
          : indexingStatus === "indexing"
            ? "Indexing"
            : categoryExtractionStatus === "extracting" ||
                categoryExtractionStatus === "pending"
              ? "Extracting Metadata"
              : "Processing";

    return (
      <Badge variant="secondary" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" />
        {showDetail ? currentStage : "Processing"}
      </Badge>
    );
  }

  // Pending
  return (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      <Clock className="h-3 w-3" />
      Pending
    </Badge>
  );
}
